<template>
  <div>
    <div class="rate-header pb-2 px-2">
      <div>
        <el-form
          v-model="filters"
          inline
          label-position="top"
          size="mini"
        >
          <el-form-item
            label="Services"
            prop="carrier_service"
          >
            <el-select
              v-model="filters.carrier_service"
              filterable
              :loading="isLoadingServices"
              value-key="id"
              clearable
            >
              <el-option
                v-for="service in services"
                :key="service.id"
                :label="service.name"
                :value="service.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item
            label="Zone From"
            prop="zone_from_desc"
          >
            <el-select
              v-model="filters.zone_from_desc"
              filterable
              remote
              :remote-method="searchZones"
              :loading="isLoadingZones"
              clearable
            >
              <el-option
                v-for="zone in zones"
                :key="zone.id"
                :label="zone.zone.desc"
                :value="zone.zone.desc"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            label="Zone To"
            prop="zone_to_desc"
          >
            <el-select
              v-model="filters.zone_to_desc"
              filterable
              remote
              :remote-method="searchZones"
              :loading="isLoadingZones"
              clearable
            >
              <el-option
                v-for="zone in zones"
                :key="zone.id"
                :label="zone.zone.desc"
                :value="zone.zone.desc"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="Show Deleted">
            <el-radio-group v-model="filters.by_deleted">
              <el-radio-button :label="null">
                No
              </el-radio-button>
              <el-radio-button :label="1">
                Both
              </el-radio-button>
              <el-radio-button :label="2">
                Yes
              </el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item>
            <template #label>
              &nbsp;
            </template>

            <el-button-group>
              <el-button
                @click.native="resetFilters"
              >
                Reset
              </el-button>
              <el-button
                type="primary"
                @click.native="loadTable"
              >
                Apply
              </el-button>
            </el-button-group>
          </el-form-item>
        </el-form>
      </div>

      <div class="text-right">
        <el-button
          v-if="canCreate"
          size="small"
          round
          @click.native="createRate"
        >
          <FontAwesomeIcon :icon="['fal', 'plus']" />&nbsp;Create
        </el-button>

        <el-button
          size="small"
          round
          @click.native="loadTable"
        >
          <FontAwesomeIcon :icon="['fal', 'sync']" />&nbsp;Refresh
        </el-button>
      </div>
    </div>

    <el-card shadow="never">
      <el-table
        v-loading="isLoading"
        size="mini"
        :data="rates"
        stripe
      >
        <el-table-column type="expand">
          <template slot-scope="props">
            <RateCardBreaksTable :rate="props.row" />
          </template>
        </el-table-column>

        <el-table-column
          label="Service"
          prop="carrier_service"
          :formatter="(row, col, cellValue) => cellValue ? (cellValue.name || '-') : '-' "
          min-width="140px"
          show-overflow-tooltip
        />

        <el-table-column
          label="Shipping Code"
          prop="carrier_service"
          min-width="120px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? (cellValue.chainit || '-') : '-' "
        />

        <el-table-column
          label="From"
          prop="zone_from"
          min-width="80px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? (cellValue.desc || '-') : '-' "
        />

        <el-table-column
          label="To"
          prop="zone_to"
          min-width="80px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? (cellValue.desc || '-') : '-' "
        />

        <el-table-column
          label="Accum"
          prop="accumulative"
          min-width="80px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? 'Yes' : 'No' "
        />

        <el-table-column
          label="Recip"
          prop="reciprocal"
          min-width="80px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? 'Yes' : 'No' "
        />

        <el-table-column
          label="Cubic"
          prop="cubic"
          min-width="80px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? (cellValue || '0') : '0' "
        />

        <el-table-column
          label="Min"
          prop="min_charge"
          min-width="80px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? (cellValue || '0') : '0' "
        />

        <el-table-column
          label="Unit Size"
          prop="unit_size"
          min-width="80px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? (cellValue || '0') : '0' "
        />

        <el-table-column
          label="Cube Limit"
          prop="cube_limit"
          min-width="90px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? (cellValue || '0') : '0' "
        />

        <el-table-column
          label="KG Limit"
          prop="weight_limit"
          min-width="80px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? (cellValue || '0') : '0' "
        />

        <el-table-column
          label="Rounding"
          prop="round_weights_to"
          min-width="80px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? (cellValue || '0') : '0' "
        />

        <el-table-column
          label="Deleted At"
          prop="deleted"
          min-width="120px"
          show-overflow-tooltip
          :formatter="(row, col, cellValue) => cellValue ? $moment(row.deleted_date).format('DD/MM/YYYY') : '-' "
        />

        <el-table-column
          v-if="canUpdate || canDelete"
          label="Actions"
          align="right"
          min-width="160px"
        >
          <template slot-scope="scope">
            <el-button
              v-if="canUpdate"
              size="mini"
              @click.native="editRate(scope.$index, scope.row)"
            >
              Edit
            </el-button>
            <el-button
              v-if="canDelete"
              size="mini"
              type="danger"
              @click.native="deleteRate(scope.$index, scope.row)"
            >
              Delete
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="text-center pt-3"
        :current-page.sync="page"
        :page-size.sync="perPage"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        @size-change="loadTable()"
        @current-change="loadTable()"
      />
    </el-card>
    <!-- Edit Form -->
    <RateCardRateEditForm
      v-if="isShowEditForm"
      ref="editForm"
      :is-show.sync="isShowEditForm"
      :carrier-id="rateCard.carrier.id"
      :rate-card="rateCard"
      :rate="rate"
      @saved="loadTable"
    />
  </div>
</template>

<script>
import RateCardRateEditForm from '@/components/Application/Manage/RateCards/RateCardRateEditForm'
import RateCardBreaksTable from '@/components/Application/Manage/RateCards/RateCardBreaksTable'
import { CREATE, UPDATE, DELETE } from '@/plugins/user'
export default {
  components: {
    RateCardRateEditForm,
    RateCardBreaksTable
  },
  props: {
    rateCard: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      rate: null,
      rates: [],
      page: 1,
      perPage: 10,
      total: 0,
      isLoading: false,
      filterVisible: false,
      isShowEditForm: false,
      isLoadingServices: false,
      services: [],
      isLoadingZones: false,
      zones: [],
      filters: {
        carrier_service: undefined,
        zone_from_desc: undefined,
        zone_to_desc: undefined,
        by_deleted: null,
        price_break_by_deleted: undefined
      }
    }
  },
  computed: {
    canCreate () {
      return this.$user.can(CREATE, 'ratecard_rate')
    },
    canUpdate () {
      return this.$user.can(UPDATE, 'ratecard_rate')
    },
    canDelete () {
      return this.$user.can(DELETE, 'ratecard_rate')
    }
  },
  created () {
    this.loadTable()
    this.loadLists()
  },
  methods: {
    loadTable () {
      this.isLoading = true
      this.filterVisible = false
      this.filters.price_break_by_deleted = this.filters.by_deleted
      const nonUndefinedFilters = Object.keys(this.filters).filter(key => this.filters[key] !== undefined).reduce((acc, key) => {
        acc[key] = this.filters[key]
        return acc
      }, {})
      this.$sdk.ratecard_rate.list(builder => {
        builder.filters = Object.assign({}, { rate_card: this.rateCard.id }, nonUndefinedFilters)
        builder.include = ['price_breaks', 'zone_from', 'zone_to', 'carrier_service']
        builder.page = this.page
        builder.limit = this.perPage
        builder.order = 'zone_from__desc:asc'
        // builder.filters = this.filters
      }).then(res => {
        this.rates = res
        this.total = res.total
      }).finally(() => {
        this.isLoading = false
      })
    },
    loadLists () {
      // Services
      this.isLoadingServices = true
      this.$sdk.carrier_service.list(builder => {
        builder.context = { resource: 'carrier', id: this.rateCard.carrier.id }
        builder.limit = 300
        builder.order = 'service_name:asc'
      }).then(res => {
        this.services = res
      })
        .finally(() => {
          this.isLoadingServices = false
        })
      // Zones
      this.isLoadingZones = true
      this.$sdk.locationcarrierzone.list(builder => {
        builder.context = { resource: 'pivot' }
        builder.include = ['zone']
        builder.filters = { pivot_location_carrier_zone_carrier: this.rateCard.carrier.id, pivot_location_carrier_zone_grouped: true }
        builder.limit = 5000
        builder.order = 'zone.desc:asc'
      }).then(res => {
        this.zones = res
      })
        .finally(() => {
          this.isLoadingZones = false
        })
    },
    searchZones (query) {
      if (query !== '') {
        this.isLoadingZones = true
        this.$sdk.locationcarrierzone.list(builder => {
          builder.context = { resource: 'pivot' }
          builder.include = ['zone']
          builder.filters = { pivot_location_carrier_zone_carrier: this.rateCard.carrier.id, pivot_location_carrier_zone_grouped: true, zone__desc__icontains: query }
          builder.limit = 5000
          builder.order = 'zone.desc:asc'
        }).then(res => {
          this.zones = res
        })
          .finally(() => {
            this.isLoadingZones = false
          })
      } else {
        this.loadLists()
      }
    },
    createRate () {
      this.rate = new this.$sdk.ratecard_rate()
      this.isShowEditForm = true
    },
    editRate (index, row) {
      this.rate = row
      this.isShowEditForm = true
    },
    deleteRate (index, row) {
      this.$confirm('Are you sure you want to delete this Rate?', 'Notice', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true,
        showClose: false,
        type: 'warning'
      }).then(() => {
        this.isLoading = true
        row.delete()
          .then(() => this.$message({ message: 'Successfully deleted', type: 'success' }))
          .finally(() => this.loadTable())
      }).catch(() => {})
    },
    resetFilters () {
      this.filters = {
        carrier_service: undefined,
        zone_from_desc: undefined,
        zone_to_desc: undefined,
        by_deleted: null,
        price_break_by_deleted: undefined
      }
      this.loadTable()
    }
  }
}
</script>

<style lang="scss" scoped>
.rate-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .rate-heading {
      margin: 0;
    }
}
</style>
